import React, { useMemo } from 'react';
import useKeyboardFormAnimation from 'src/presentation/hooks/useKeyboardFormAnimation';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import { View, StyleSheet, Animated, ScrollView } from 'react-native';
import type { ImageStyle } from 'react-native';
import type { RouteProp } from '@react-navigation/native';
import { useRoute } from '@react-navigation/native';
import NavBar from 'src/presentation/components/organisms/NavBar';
import Heading from 'src/presentation/components/atoms/Heading';
import Paragraph from 'src/presentation/components/atoms/Paragraph';
import ForgotPasswordForm from 'src/presentation/components/organisms/forms/ForgotPasswordForm';
import { useFormStore } from 'src/domain/stores/useFormStore';
import { useEventStore } from 'src/domain/stores/useEventStore';
import Colors from 'src/core/utils/Colors';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import Images from 'src/core/utils/Images';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import Constants from 'src/core/utils/Constants';

function ForgotPasswordScreen() {
	const route = useRoute<RouteProp<RootStackParamList, typeof Constants.screenForgotPassword>>();
	const formStore = useFormStore();
	const eventStore = useEventStore();
	const { keyboardHeight, imageHeight } = useKeyboardFormAnimation();
	const contentWidth = useContentWidth();

	const sizedStyles = useMemo((): { img: ImageStyle } => ({ img: { width: contentWidth } }), [contentWidth]);

	return (
		<View style={styles.root}>
			{formStore.loginFromForm ? (
				<NavBar
					backBtn={true}
					bgImage={eventStore.activeTheme.header_bg_image}
					bgcolor={eventStore.activeTheme.header_bg_color}
					color={eventStore.activeTheme.header_text_color}
					editBtn={false}
					statusBarStyle={eventStore.activeTheme.statusbar_color}
					title={Translations.titleForgotPassword}
				/>
			) : (
				<NavBar
					backBtn={true}
					bgcolor={Colors.primary}
					centerTitle={true}
					color={Colors.black}
					editBtn={false}
					title={Translations.titleForgotPassword}
				/>
			)}

			<ScrollView keyboardShouldPersistTaps="always" style={styles.scroll}>
				<Animated.View style={{ paddingBottom: keyboardHeight }}>
					{(route?.name as string) === Constants.screenLostPasswordPage ? null : (
						<Animated.Image
							resizeMode="cover"
							source={Images.loginHeader}
							style={[styles.img, sizedStyles.img, { height: imageHeight }]}
						/>
					)}
					<View style={styles.contentContainer}>
						<Heading styles={styles.title} text={Translations.buttonForgotPassword} />
						<Paragraph text={Translations.textForgotPasswordDescription} />
					</View>
					<ForgotPasswordForm />
				</Animated.View>
			</ScrollView>
		</View>
	);
}

export default ForgotPasswordScreen;

const styles = StyleSheet.create({
	root: {
		flex: 1,
		backgroundColor: Colors.white,
	},
	scroll: {
		backgroundColor: Colors.white,
	},
	img: {
		height: 200,
	},
	contentContainer: {
		paddingLeft: Dimens.padding20,
		paddingRight: Dimens.padding20,
		paddingTop: Dimens.padding20,
	},
	title: {},
});
